import { ReactNode, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider'

interface ProtectedRouteProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * ProtectedRoute component that checks authentication status and redirects to login if not authenticated.
 * 
 * Requirements fulfilled:
 * - 7.4: IF tokens are valid, THEN THE Portal System SHALL restore the User's authenticated session
 * - 7.5: IF tokens are expired or invalid, THEN THE Portal System SHALL redirect the User to the login page
 */
export default function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading, user } = useAuth()

  useEffect(() => {
    // Don't redirect while still loading authentication state
    if (isLoading) {
      return
    }

    // If not authenticated, redirect to login page (Requirement 7.5)
    if (!isAuthenticated) {
      // Preserve the current path as redirect parameter
      const currentPath = location.pathname + location.search
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true })
      return
    }

    // If authenticated but no user data, something went wrong - redirect to login
    if (isAuthenticated && !user) {
      console.warn('Authenticated but no user data available, redirecting to login')
      const currentPath = location.pathname + location.search
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true })
      return
    }

    // If we reach here, user is properly authenticated (Requirement 7.4)
  }, [isAuthenticated, isLoading, user, navigate, location])

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Loading...
            </h3>
            <p className="text-gray-600">
              Checking authentication status
            </p>
          </div>
        </div>
      )
    )
  }

  // Don't render children if not authenticated (will redirect)
  if (!isAuthenticated || !user) {
    return (
      fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Redirecting...
            </h3>
            <p className="text-gray-600">
              Please wait while we redirect you to the login page.
            </p>
          </div>
        </div>
      )
    )
  }

  // User is authenticated and has valid session, render protected content (Requirement 7.4)
  return <>{children}</>
}